//
import { RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import React, { useEffect, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { StackParamList } from '../../../App';
import pedidoApi from '../../apis/pedidoApi';
import colors from '../../colors';
import { SIDE_PADDING, TOP_PADDING, TXT_G, TXT_M } from '../../constants';
import { useAlert } from '../../redux/alert/alertHooks';
import { useEmpresa } from '../../redux/empresa/empresaHooks';
import { useDadosPedido, usePedido } from '../../redux/pedido/pedidoHooks';
import { useSessao } from '../../redux/sessao/sessaoHooks';
import Cliente from '../../shared/entity/Cliente';
import {
  calcularTotalDesconto,
  calcularTotalPedido,
  calcularTotalPedidoSemDesconto,
  formatarDinheiro
} from '../../shared/helper';
import AlertDialog from '../Generic/AlertDialog';
import CTextInput from '../Generic/CTextInput';
import Divider from '../Generic/Divider';
import Header from '../Generic/Header';
import PageContainer from '../Generic/PageContainer';
import ScrollComBotao from '../Generic/ScrollComBotao';
import Title from '../Generic/Title';
import InputCupom from './InputCupom';
import ItemProdutoNoPedido from './ItemProdutoNoPedido';
import ItemProdutoCompostoNoPedido from './ItemProdutoCompostoNoPedido';
import ScrollEnderecos from './ScrollEnderecos';
import SelecaoCliente from './SelecaoCliente';
import SelecaoComanda from './SelecaoComanda';
import SelecaoTipoPagamento from './SelecaoTipoPagamento';

type Props = {
  navigation: StackNavigationProp<StackParamList, 'PedidoPage'>;
  route: RouteProp<StackParamList, 'PedidoPage'>;
};

const PedidoPage: React.FC<Props> = (props) => {
  const { navigation, route } = props;

  const [pedido, setPedido, limparPedido] = usePedido();
  const [setEnderecoPedido, setObservacaoPedido] = useDadosPedido();
  const [mostrarModalFecharPedido, setMostrarModalFecharPedido] = useState(false);
  const [temEntrega, setTemEntrega] = useState(true);

  const [sessao] = useSessao();
  const [empresa] = useEmpresa();
  const [mostrarAlert] = useAlert();

  const [total, setTotal] = useState(0);

  const cliente: null | Cliente = pedido.cliente;
  const setCliente = (cliente: Cliente) => {
    setPedido({ ...pedido, cliente, clienteId: cliente.id });
  };

  useEffect(() => {
    if (sessao.cliente) {
      if (!cliente || sessao.cliente.nivel === 0) {
        setCliente(sessao.cliente);
      }
    }
  }, [sessao]);

  useEffect(() => {
    setTotal(calcularTotalPedido(pedido, empresa.precoDelivery, empresa.valorMinimoDeliveryGratis));
  }, [pedido]);

  useEffect(() => {
    if (sessao.cliente && sessao.cliente.nivel > 0) {
      setTemEntrega(false);
      setEnderecoPedido(null);
    }
  }, []);

  const validarPedido = () => {
    const isAdmin = sessao.cliente && sessao.cliente.nivel > 0;
    if (!isAdmin && calcularTotalPedido(pedido, 0) < empresa.pedidoMinimo) {
      mostrarAlert('Erro', 'Valor do pedido é menor que o mínimo.');
      return false;
    }
    if (temEntrega && !pedido.endereco) {
      mostrarAlert('Erro', 'Selecione o endereço de entrega.');
      return false;
    }
    if (!temEntrega && pedido.endereco) {
      setEnderecoPedido(null);
    }
    return true;
  };

  const verificarPedido = async () => {
    if (!empresa || !empresa.aberto) {
      mostrarAlert('Ah não :(', 'Estamos fechados no momento...');
      return;
    }

    if (!sessao.token || !sessao.cliente) {
      navigation.navigate('LoginPage');
      return;
    }

    if (empresa.verificaClientes && !sessao.cliente.verificado) {
      navigation.navigate('VerificacaoPage');
      return;
    }

    try {
      if (validarPedido()) {
        setMostrarModalFecharPedido(true);
      }
    } catch (err) {
      console.log('err', err);
      mostrarAlert('Erro', `Ocorreu um erro ao processar seu pedido: ${err.data || err.message}`);
    }
  };

  const fecharPedido = async () => {
    try {
      const totalLiquido = calcularTotalPedido(
        pedido,
        empresa.precoDelivery,
        empresa.valorMinimoDeliveryGratis
      );
      const totalBruto = calcularTotalPedidoSemDesconto(
        pedido,
        0,
        empresa.valorMinimoDeliveryGratis
      );
      const res = await pedidoApi.insert({
        ...pedido,
        empresa: empresa,
        precoDelivery: empresa.precoDelivery,
        totalBruto: totalBruto,
        totalLiquido: totalLiquido,
        valorDesconto: calcularTotalDesconto(pedido, pedido.precoDelivery),
        trocoPara: pedido.trocoPara > totalLiquido ? pedido.trocoPara : totalLiquido,
        clienteId: cliente?.id
      });
      limparPedido();
      navigation.replace('EsperaPedidoPage', { pedidoId: res.id });
    } catch (err) {
      console.log('err', err);
      mostrarAlert(
        'Erro',
        err.data ?? 'Ocorreu um erro ao processar seu pedido. Por favor, tente novamente.'
      );
    }
  };

  // if (pedido.produtosNoPedido.length === 0) {
  //   return (
  //     <PageContainer header={<Header titulo={'Pedido'} goBack />} scrollView={false}>
  //       <View style={{ padding: 30, alignItems: 'center' }}>
  //         <FontAwesome5
  //           name="sad-tear"
  //           size={60}
  //           color={colors.darkGrey}
  //           style={{ marginBottom: 10 }}
  //         />
  //         <Text style={styles.txtPedidoVazio}>Nenhum produto adicionado ao pedido</Text>
  //       </View>
  //     </PageContainer>
  //   );
  // }

  const getTituloBotao = () => {
    if (!empresa || !empresa.aberto) {
      return 'ESTAMOS FECHADOS';
    }
    if (sessao.token) {
      return `FECHAR PEDIDO (R$ ${formatarDinheiro(total)})`;
    }
    return 'FAZER LOGIN';
  };

  return (
    <PageContainer header={<Header titulo={'Pedido'} goBack />} scrollView={false}>
      <AlertDialog
        titulo={'Fechar pedido'}
        descricao={
          'Tem certeza que deseja fechar o pedido?\nDepois de fechado, NÃO poderão ser feitas alterações no pedido.'
        }
        show={mostrarModalFecharPedido}
        onClose={() => setMostrarModalFecharPedido(false)}
        actions={[
          {
            titulo: 'Não',
            onPress: () => setMostrarModalFecharPedido(false)
          },
          {
            titulo: 'Sim',
            onPress: () => fecharPedido(),
            color: colors.green,
            dark: true
          }
        ]}
      />

      <ScrollComBotao
        buttonColor={empresa?.aberto ? colors.green : colors.red}
        buttonTitle={getTituloBotao()}
        onPress={verificarPedido}
        containerStyle={{ paddingTop: TOP_PADDING }}
      >
        <View style={{ paddingLeft: SIDE_PADDING, paddingRight: SIDE_PADDING }}>
          {empresa.usaComandas && sessao.cliente && sessao.cliente.nivel > 0 && (
            <SelecaoComanda
              comanda={pedido.comanda}
              setComanda={(comanda) => setPedido({ ...pedido, comanda })}
            />
          )}

          {sessao.cliente && sessao.cliente.nivel > 0 && (
            <SelecaoCliente cliente={cliente} setCliente={setCliente} />
          )}

          <Title style={{ marginBottom: 10 }}>Seu pedido:</Title>

          {pedido.produtosNoPedido.length === 0 &&
            pedido.produtosCompostosNoPedido.length === 0 && (
              <Text style={{ alignSelf: 'center' }}>Nenhum produto adicionado</Text>
            )}

          {pedido.produtosNoPedido.map((produto, index) => (
            <ItemProdutoNoPedido
              produto={produto}
              index={index}
              key={produto.id + 'index' + index}
            />
          ))}

          {pedido.produtosCompostosNoPedido.map((composto, index) => (
            <ItemProdutoCompostoNoPedido
              composto={composto}
              index={index}
              key={composto.id + 'index' + index}
            />
          ))}

          <Divider style={{ marginBottom: 10, marginTop: 10 }} />

          <ScrollEnderecos
            clienteId={cliente?.id}
            temEntrega={temEntrega}
            setTemEntrega={setTemEntrega}
          />

          {/* TIPO PAGEMENTO */}
          <SelecaoTipoPagamento
            tipoPagamento={pedido.tipoPagamento}
            setTipoPagamento={(v) => setPedido({ ...pedido, tipoPagamento: v })}
            trocoPara={pedido.trocoPara}
            setTrocoPara={(v) => setPedido({ ...pedido, trocoPara: v })}
          />

          <Title style={{ marginBottom: 10 }}>Observação:</Title>
          <CTextInput onChangeText={(txt) => setObservacaoPedido(txt)} value={pedido.observacao} />

          <InputCupom />
        </View>
      </ScrollComBotao>
    </PageContainer>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingLeft: SIDE_PADDING,
    paddingRight: SIDE_PADDING,
    paddingTop: TOP_PADDING
  },
  botao: {
    backgroundColor: colors.green,
    width: '100%',
    height: 55,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    position: 'absolute',
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center'
  },
  txtBotao: {
    color: '#fff',
    fontSize: TXT_M
  },
  txtPedidoVazio: {
    fontSize: TXT_G,
    textAlign: 'center',
    color: colors.darkGrey
  },
  titulo: {
    fontSize: TXT_G,
    marginBottom: 10
  }
});

export default PedidoPage;
